/**
 * Mock accountability service.
 * Mirrors the real accountability.service.js API with in-memory data.
 */
import { MOCK_PARTNERSHIP, MOCK_CHECKINS } from '../../data/mock/mock-accountability.data';
import { MOCK_USERS } from '../../data/mock/mock-users.data';
import { mockDelay, mockLog, mockId } from './mock-delay';

// In-memory state
let partnership = { ...MOCK_PARTNERSHIP };
let checkIns = [...MOCK_CHECKINS];

export const findPartner = async (userId) => {
  mockLog('AccountabilityService', 'findPartner', userId);
  await mockDelay();
  const candidates = MOCK_USERS.filter((u) => u.id !== userId && !u.accountabilityPartnerId);
  if (candidates.length === 0) return null;
  return candidates[Math.floor(Math.random() * candidates.length)];
};

export const sendPartnerRequest = async (fromUserId, toUserId, fromUserData, toUserData) => {
  mockLog('AccountabilityService', 'sendPartnerRequest', { from: fromUserId, to: toUserId });
  await mockDelay();
  return {
    id: `mock_pair_${mockId()}`,
    users: [fromUserId, toUserId],
    userNames: [fromUserData.displayName || '', toUserData.displayName || ''],
    userUsernames: [fromUserData.username || '', toUserData.username || ''],
    userAvatars: [fromUserData.avatarUrl || '', toUserData.avatarUrl || ''],
    status: 'pending',
    createdAt: new Date().toISOString(),
    endedAt: null,
  };
};

export const acceptPartnerRequest = async (pairId) => {
  mockLog('AccountabilityService', 'acceptPartnerRequest', pairId);
  await mockDelay();
  partnership = { ...partnership, status: 'active' };
};

export const declinePartnerRequest = async (pairId) => {
  mockLog('AccountabilityService', 'declinePartnerRequest', pairId);
  await mockDelay();
};

export const endPartnership = async (pairId) => {
  mockLog('AccountabilityService', 'endPartnership', pairId);
  await mockDelay();
  partnership = { ...partnership, status: 'ended', endedAt: new Date().toISOString() };
};

export const getPartnership = async (userId) => {
  mockLog('AccountabilityService', 'getPartnership', userId);
  await mockDelay();
  if (partnership.status === 'active' && partnership.users.includes(userId)) {
    return { ...partnership };
  }
  return null;
};

export const getPendingRequests = async (userId) => {
  mockLog('AccountabilityService', 'getPendingRequests', userId);
  await mockDelay();
  return [];
};

export const submitCheckIn = async (pairId, checkInData) => {
  mockLog('AccountabilityService', 'submitCheckIn', checkInData);
  await mockDelay();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const checkIn = {
    id: `mock_ci_${mockId()}`,
    userId: checkInData.userId,
    date: today.toISOString(),
    mood: checkInData.mood || 'okay',
    workedOut: checkInData.workedOut || false,
    ateMeal: checkInData.ateMeal || false,
    message: checkInData.message || '',
    createdAt: new Date().toISOString(),
    _docSnapshot: null,
  };
  checkIns = [checkIn, ...checkIns];
  return checkIn;
};

export const getCheckIns = async (pairId, lastDoc = null, pageLimit = 14) => {
  mockLog('AccountabilityService', 'getCheckIns', { pairId, lastDoc, pageLimit });
  await mockDelay();
  const sorted = checkIns.sort((a, b) => new Date(b.date) - new Date(a.date));
  let startIndex = 0;
  if (lastDoc) {
    const idx = sorted.findIndex((c) => c.id === lastDoc);
    if (idx !== -1) startIndex = idx + 1;
  }
  const page = sorted.slice(startIndex, startIndex + pageLimit);
  const newLastDoc = page.length > 0 ? page[page.length - 1].id : null;
  const hasMore = startIndex + pageLimit < sorted.length;
  return { checkIns: page, lastDoc: newLastDoc, hasMore };
};

export const getTodayCheckIn = async (pairId, userId) => {
  mockLog('AccountabilityService', 'getTodayCheckIn', { pairId, userId });
  await mockDelay(50, 200);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  return checkIns.find((c) => {
    const d = new Date(c.date);
    return c.userId === userId && d >= today && d < tomorrow;
  }) || null;
};
